import { useState, useEffect } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useProjects } from "@/hooks/useProjects";
import type { Project } from "@/lib/types";

const PRESET_COLORS = [
  { name: "Forest Green", value: "#2D6A4F" },
  { name: "Olive", value: "#606C38" },
  { name: "Copper", value: "#C2703E" },
  { name: "Slate", value: "#64748B" },
  { name: "Blue", value: "#2563EB" },
  { name: "Purple", value: "#7C3AED" },
  { name: "Red", value: "#DC2626" },
  { name: "Amber", value: "#D97706" },
];

export type ProjectFormData = {
  name: string;
  slug: string;
  color: string;
  status: Project["status"];
  description: string | null;
  deadline: string | null;
  url: string | null;
  parent_id: string | null;
};

type ProjectFormProps = {
  project?: Project | null;
  onSubmit: (data: ProjectFormData) => void;
  onCancel: () => void;
  isPending?: boolean;
};

function toSlug(name: string) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export function ProjectForm({ project, onSubmit, onCancel, isPending }: ProjectFormProps) {
  const [name, setName] = useState(project?.name || "");
  const [slug, setSlug] = useState(project?.slug || "");
  const [color, setColor] = useState(project?.color || PRESET_COLORS[0].value);
  const [status, setStatus] = useState<Project["status"]>(project?.status || "active");
  const [description, setDescription] = useState(project?.description || "");
  const [deadline, setDeadline] = useState<Date | undefined>(
    project?.deadline ? new Date(project.deadline + "T00:00:00") : undefined
  );
  const [url, setUrl] = useState(project?.url || "");
  const [parentId, setParentId] = useState<string | null>(project?.parent_id || null);
  const [slugEdited, setSlugEdited] = useState(false);
  const { data: allProjects } = useProjects();

  // Root projects that can be parents (exclude self and projects that already have a parent)
  const parentOptions = (allProjects || []).filter(
    (p) => p.parent_id === null && p.id !== project?.id
  );

  // Auto-generate slug from name unless user has manually edited it
  useEffect(() => {
    if (!slugEdited) {
      setSlug(toSlug(name));
    }
  }, [name, slugEdited]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      slug: slug || toSlug(name),
      color,
      status,
      description: description.trim() || null,
      deadline: deadline ? format(deadline, "yyyy-MM-dd") : null,
      url: url.trim() || null,
      parent_id: parentId,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Name */}
      <div className="space-y-2">
        <Label htmlFor="project-name">Project Name</Label>
        <Input
          id="project-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., BCM, SEP, One Love"
          autoFocus
        />
      </div>

      {/* Slug */}
      <div className="space-y-2">
        <Label htmlFor="project-slug">Slug</Label>
        <Input
          id="project-slug"
          value={slug}
          onChange={(e) => {
            setSlug(e.target.value);
            setSlugEdited(true);
          }}
          placeholder="auto-generated"
          className="text-muted-foreground"
        />
        <p className="text-xs text-muted-foreground">
          Used for tagging items. Auto-generated from name.
        </p>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="project-description">Description</Label>
        <Textarea
          id="project-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What is this project about?"
          rows={3}
        />
      </div>

      {/* URL */}
      <div className="space-y-2">
        <Label htmlFor="project-url">URL</Label>
        <Input
          id="project-url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="e.g., bcm.hellaintel.com"
        />
        <p className="text-xs text-muted-foreground">
          Link to the live project or repo.
        </p>
      </div>

      {/* Parent Project */}
      {parentOptions.length > 0 && (
        <div className="space-y-2">
          <Label>Parent Project</Label>
          <Select
            value={parentId || "__none__"}
            onValueChange={(v) => setParentId(v === "__none__" ? null : v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="None (top-level)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None (top-level)</SelectItem>
              {parentOptions.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Group this project under a parent.
          </p>
        </div>
      )}

      {/* Deadline */}
      <div className="space-y-2">
        <Label>Deadline</Label>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !deadline && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {deadline ? format(deadline, "MMM d, yyyy") : "No deadline"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={deadline}
                onSelect={setDeadline}
              />
            </PopoverContent>
          </Popover>
          {deadline && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setDeadline(undefined)}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Color */}
      <div className="space-y-2">
        <Label>Color</Label>
        <div className="flex flex-wrap gap-2">
          {PRESET_COLORS.map((preset) => (
            <button
              key={preset.value}
              type="button"
              className={`w-8 h-8 rounded-full border-2 transition-all ${
                color === preset.value
                  ? "border-foreground scale-110"
                  : "border-transparent hover:border-muted-foreground/50"
              }`}
              style={{ backgroundColor: preset.value }}
              onClick={() => setColor(preset.value)}
              title={preset.name}
            />
          ))}
        </div>
      </div>

      {/* Status (only show when editing) */}
      {project && (
        <div className="space-y-2">
          <Label>Status</Label>
          <RadioGroup
            value={status}
            onValueChange={(v) => setStatus(v as Project["status"])}
          >
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="active" id="status-active" />
                <Label htmlFor="status-active">Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="paused" id="status-paused" />
                <Label htmlFor="status-paused">Paused</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="archived" id="status-archived" />
                <Label htmlFor="status-archived">Archived</Label>
              </div>
            </div>
          </RadioGroup>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <Button type="submit" className="flex-1" disabled={isPending || !name.trim()}>
          {project ? "Save Changes" : "Create Project"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
